import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  saveDocument,
  deleteDocument,
  viewDocument,
  saveDocumentList,
  getS3SignUploadUrl,
  uploadDirectlyToS3,
} from '../../../services/uploadDocuments';

export const QUERY_KEYS = {
  documents: (folderName) => ['documents', folderName],
};

export const useSaveDocument = (folderName, { onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, documentPath, isExisting, applicationId }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folderName', folderName);
      // documentName is simply the original file name picked by the user from their system
      formData.append('documentName', file.name);
      formData.append('documentPath', documentPath || '');

      if (isExisting) {
        formData.append('applicationId', applicationId);
      }

      return saveDocument(formData);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(folderName) });
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

export const useNewSaveDocument = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      documentPath,
      folderName,
      isExisting,
      applicationId,
      onUploadProgress,
    }) => {
      // Step 1 — ask the backend for a pre-signed S3 POST policy.
      const signPayload = {
        fileName: file.name,
        contentType: file.type,
        folderName,
        documentName: file.name,
        documentPath: documentPath || '',
        fileSize: file.size,
      };

      if (isExisting) {
        signPayload.applicationId = applicationId;
      }

      const { documentId, uploadUrl, uploadFields } = await getS3SignUploadUrl(signPayload);

      // Step 2 — upload the bytes directly to S3 using the signed fields.
      // S3's POST policy requires the "file" field to be the LAST field
      // in the multipart body, after all the policy/signature fields.
      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      await uploadDirectlyToS3(uploadUrl, formData, onUploadProgress);

      // S3 replies 204 No Content on success, so there's nothing to read back —
      // just carry forward the documentId we already got from the sign step.
      return { documentId };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(variables.folderName) });
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

export const useDeleteDocument = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, applicationId }) => deleteDocument({ documentId, applicationId }),

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(variables.applicationId) });
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

export const useViewDocument = ({ onSuccess, onError } = {}) => {
  return useMutation({
    mutationFn: ({ documentId, applicationId }) => viewDocument({ documentId, applicationId }),

    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

// export const useSaveDocumentList = ({ onSuccess, onError } = {}) => {
//   return useMutation({
//     mutationFn: ({ title, folderName, pdfArray, description }) =>
//       saveDocumentList({ title, folderName, pdfArray, description }),
//     onSuccess: (data, variables) => onSuccess?.(data, variables),
//     onError: (error, variables) => onError?.(error, variables),
//   });
// };

export const useSaveDocumentList = ({ onSuccess, onError } = {}) => {
  return useMutation({
    mutationFn: ({ folderArray, pdfArray }) => saveDocumentList({ folderArray, pdfArray }),
    onSuccess: (data, variables) => onSuccess?.(data, variables),
    onError: (error, variables) => onError?.(error, variables),
  });
};
